import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ApiAgentService} from '../../services/api-agent.service';
import {UtilsService} from '../../services/utils.service';
import {MerchantConsentEvent, MerchantConsentEventRow, MerchantConsentState} from '../../models/merchant-consent';

/**
 * Privacy & Consent (Roadmap #34). Marketing is self-service (auto-applied consent_change
 * ticket); processing is read-only — withdrawing it terminates the account, support only.
 * Identity is never sent from here; the API derives the merchant from the JWT.
 */
@Component({
  selector: 'app-privacy-consent',
  templateUrl: './privacy-consent.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, RouterModule],
  styleUrls: ['./privacy-consent.component.scss']
})
export class PrivacyConsentComponent implements OnInit {

  loading = true;
  saving = false;
  /** False until the register has actually been read — the defaults below are not consent state. */
  loaded = false;

  processingActive = true;
  marketingActive = false;
  history: MerchantConsentEventRow[] = [];

  errorMessage = '';
  referenceTicketId: number | null = null;

  /** How many events the screen shows; the register keeps every one. */
  readonly historyLimit = 5;

  readonly eventLabels: { [key: string]: string } = {
    given: 'Consent given',
    withdrawn: 'Consent withdrawn'
  };

  readonly typeLabels: { [key: string]: string } = {
    marketing: 'Marketing communications',
    processing: 'Processing of personal data'
  };

  readonly channelLabels: { [key: string]: string } = {
    onboarding: 'At registration',
    portal: 'From this portal',
    support: 'Through support',
    api: 'Through the API'
  };

  constructor(private api: ApiAgentService,
              private utils: UtilsService,
              private translate: TranslateService) {
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    try {
      this.apply(await this.api.getMyConsent());
      this.loaded = true;
    } catch (e) {
      // Leave loaded false: the view hides the rows rather than presenting the field defaults
      // as if they were this merchant's real consent state.
      this.errorMessage = this.translate.instant('Could not load your consent settings. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  private apply(state: MerchantConsentState): void {
    this.processingActive = state.processingActive;
    this.marketingActive = state.marketingActive;
    this.history = (state.history || [])
      .slice(0, this.historyLimit)
      .map(e => ({...e, atDate: this.parseApiDate(e.at)}));
  }

  private parseApiDate(value: number | string | null | undefined): Date | null {
    return this.utils.parseApiDate(value);
  }

  eventLabel(event: MerchantConsentEvent): string {
    return this.eventLabels[event.event] || event.event;
  }

  typeLabel(type: string): string {
    return this.typeLabels[type] || type;
  }

  channelLabel(channel: string | null): string {
    return channel ? (this.channelLabels[channel] || channel) : '';
  }

  /** On failure the toggle is put back — the register, not the switch, is the truth. */
  async onMarketingToggle(requested: boolean): Promise<void> {
    this.errorMessage = '';
    this.referenceTicketId = null;
    this.saving = true;
    try {
      const result = await this.api.saveConsentChange({
        type: 'marketing',
        action: requested ? 'give' : 'withdraw'
      });
      this.marketingActive = result.marketingActive;
      this.processingActive = result.processingActive;
      this.referenceTicketId = result.ticketId;
    } catch (e: any) {
      this.marketingActive = !requested;
      this.errorMessage = this.translate.instant('Could not save your choice. Please try again.');
    } finally {
      this.saving = false;
    }
    // Outside the try: a failed refresh must not undo an accepted change; POST state stands.
    try {
      this.apply(await this.api.getMyConsent());
    } catch (e) { /* keep the state the POST returned */ }
  }
}
